import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEventSlug, useQuery } from 'hooks'
import { CardWrapper, NotFound } from 'components'
import { TopToolbar, SortableList, SortableItem } from 'components/lists'
import { _ } from 'utils'
import {
    SpaceTypeEnum,
    GetSpacesOverviewQuery,
    GetSpacesOverviewQueryVariables
} from 'generated/graphql'
import { convertDataForSortableList, getNodes } from 'graphql/utils'
import { addModal } from 'features/modals/modalSlice'
import SpaceRow from './components/SpaceRow'
import NoSpace from './components/NoSpace'
import { spacesStatusSelector } from '../events/eventSlice'
import { getSpacesOverviewQuery } from './graphql/query'
import { updateSpaceOrder } from './graphql/mutation'
import CreateSpace from './CreateSpace'

const SpacesOverview = () => {
    const dispatch = useDispatch()
    const eventSlug = useEventSlug()
    const { numberOfSpaces, maxSpaces } = useSelector(spacesStatusSelector)
    const disabledAdd = numberOfSpaces >= maxSpaces
    const [spaces, setSpaces] = useState<SortableItem[]>([])
    const [search, setSearch] = useState('')
    const [spaceType, setSpaceType] = useState<SpaceTypeEnum>(null)
    const [minOrder, setMinOrder] = useState(0)
    const disableSort = !!(search || spaceType)
    const variables: GetSpacesOverviewQueryVariables = {
        eventSlug, search
    }

    if (spaceType) {
        variables.type = spaceType
    } else {
        delete variables.type
    }

    const { data, requestStatus, fetchData } = useQuery<
        GetSpacesOverviewQuery,
        GetSpacesOverviewQueryVariables
    >(
        getSpacesOverviewQuery, variables
    )

    const options = useMemo(() => (
        [
            {
                label: _('space_overview.space_type.chat_roulette'),
                value: SpaceTypeEnum.ChatRoulette
            },
            { label: _('space_overview.space_type.cinema'), value: SpaceTypeEnum.Cinema },
            { label: _('space_overview.space_type.jobs'), value: SpaceTypeEnum.Jobs },
            { label: _('space_overview.space_type.map'), value: SpaceTypeEnum.Map },
            { label: _('space_overview.space_type.meeting'), value: SpaceTypeEnum.Meeting },
            { label: _('space_overview.space_type.networking'), value: SpaceTypeEnum.Networking },
            { label: _('space_overview.space_type.poster'), value: SpaceTypeEnum.Posters },
            {
                label: _('space_overview.space_type.presentation'),
                value: SpaceTypeEnum.Presentation
            },
        ]
    ), [])

    const renderItem = (space) => (
        <SpaceRow space={space} fetchData={fetchData} />
    )

    useEffect(() => {
        // After fetching data from API successfully, convert data for SortableList
        if (data) {
            const dataForSortableList = convertDataForSortableList(
                getNodes(data?.event?.spaces).filter((e) => e.type !== SpaceTypeEnum.Booth),
                renderItem,
                'timetableOrder'
            )
            setSpaces(dataForSortableList)
            setMinOrder(dataForSortableList[0]?.order || 0)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]) // Don't want renderItem in deps

    if (requestStatus === 'failed') {
        // Most likely wrong event slug
        return <NotFound />
    }

    const showAddModal = () => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <CreateSpace close={close} onSubmitted={fetchData} minOrder={minOrder} />
            ),
            size: 'xl',
            shouldNotWrap: true
        }))
    }
    const updateMinOrder = (order) => setMinOrder(order)

    return numberOfSpaces ? (
        <CardWrapper>
            <TopToolbar
                disabledAdd={disabledAdd}
                addItemText={_('space_overview.list.add_button')}
                onAddItem={showAddModal}
                searchPlaceHolder={_('space_overview.list.search_placeholder')}
                onSearch={setSearch}
                filters={[{
                    options,
                    onChange: setSpaceType,
                    isClearable: true,
                    placeholder: _('space_overview.filter_space_type.placeholder')
                }]}
            />
            <SortableList
                initialList={spaces}
                updateOrder={updateSpaceOrder}
                disableSort={disableSort}
                onUpdateMinOrder={updateMinOrder}
                requestStatus={requestStatus}
            />
        </CardWrapper>
    ) : (
        <NoSpace fetchData={fetchData} />
    )
}

export default SpacesOverview
